import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as analyticsService from "../services/analytics.services";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";

const Dashboard = () => {
  const { t } = useTranslation();
  const [showDashboardCount, setShowDashboardCount] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsService
      .getDashboardSummary()
      .then((response) => {
        setShowDashboardCount(response && response.data ? response.data : {});
        setLoading(false);
      })
      .catch((error) => {
        console.log("error ====> ", error);
        setLoading(false);
      });
  }, []);

  const getStat = (value) =>
    value !== undefined && value !== null ? value : "0";

  const getTransactionStatusClass = (status) => {
    if (["completed", "paid", "active"].includes(status)) return "success";
    if (["failed", "refunded", "canceled"].includes(status)) return "danger";
    return "warning";
  };

  const last7DaysRevenue =
    showDashboardCount?.performanceTrends?.last7DaysRevenue || [];

  const revenueChartOptions = {
    chart: { id: "revenue-chart", type: "area", toolbar: { show: false } },
    xaxis: {
      categories: last7DaysRevenue.map((d) =>
        new Date(d._id).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      ),
    },
    yaxis: {
      labels: {
        formatter: (val) => `$${Number(val).toFixed(2)}`,
      },
    },
    title: { text: t("Revenue (Last 7 Days)"), align: "left" },
    colors: ["#38d259"],
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 },
    },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val) => `$${Number(val).toFixed(2)}`,
      },
    },
  };

  const revenueChartSeries = [
    {
      name: t("Revenue"),
      data: last7DaysRevenue.map((d) => Number(d.dailyAmount || 0)),
    },
  ];

  return (
    <>
      <div className="row row-sm animation_fade">
        <div className="col-sm-12 col-lg-12 col-xl-12">
          <div className="row row-sm">
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <Link to={`/admin/user-management/user/list/1`}>
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24"
                          className="bi bi-people-fill"
                          viewBox="0 0 16 16"
                        >
                          <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">
                          {t("sidebar_link_users")}
                        </label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">
                            {getStat(showDashboardCount?.totalUsers)}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <div className="card-item">
                    <div className="card-item-icon card-icon cp bg-info-transparent">
                      <i className="fa fa-user-check text-info tx-22"></i>
                    </div>
                    <div className="card-item-title mb-2">
                      <label className="main-content-label tx-13 font-weight-bold mb-1">
                        {t("Today's Active Users")}
                      </label>
                    </div>
                    <div className="card-item-body">
                      <div className="card-item-stat">
                        <h4 className="font-weight-bold">
                          {getStat(
                            showDashboardCount?.activitySnapshot?.dau ??
                            showDashboardCount?.todayActiveUsers,
                          )}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <div className="card-item">
                    <div className="card-item-icon card-icon cp bg-secondary-transparent">
                      <i className="fa fa-play-circle text-secondary tx-22"></i>
                    </div>
                    <div className="card-item-title mb-2">
                      <label className="main-content-label tx-13 font-weight-bold mb-1">
                        {t("Games Played Today")}
                      </label>
                    </div>
                    <div className="card-item-body">
                      <div className="card-item-stat">
                        <h4 className="font-weight-bold">
                          {getStat(
                            showDashboardCount?.activitySnapshot
                              ?.gamesPlayedToday,
                          )}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <div className="card-item">
                    <div className="card-item-icon card-icon cp bg-primary-transparent">
                      <i className="fa fa-child text-primary tx-22"></i>
                    </div>
                    <div className="card-item-title mb-2">
                      <label className="main-content-label tx-13 font-weight-bold mb-1">
                        {t("Total Children")}
                      </label>
                    </div>
                    <div className="card-item-body">
                      <div className="card-item-stat">
                        <h4 className="font-weight-bold">
                          {getStat(showDashboardCount?.totalChildren)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <Link to={`/admin/game-management/game/list/1`}>
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp bg-success-transparent">
                        <i className="fa fa-gamepad text-success tx-22"></i>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">
                          {t("Total Games")}
                        </label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">
                            {getStat(showDashboardCount?.totalGames)}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <div className="card-item">
                    <div className="card-item-icon card-icon cp bg-warning-transparent">
                      <i className="fa fa-crown text-warning tx-22"></i>
                    </div>
                    <div className="card-item-title mb-2">
                      <label className="main-content-label tx-13 font-weight-bold mb-1">
                        {t("Active Subscriptions")}
                      </label>
                    </div>
                    <div className="card-item-body">
                      <div className="card-item-stat">
                        <h4 className="font-weight-bold">
                          {getStat(showDashboardCount?.activeSubscriptions)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <div className="card-item">
                    <div className="card-item-icon card-icon cp bg-danger-transparent">
                      <i className="fa fa-dollar-sign text-danger tx-22"></i>
                    </div>
                    <div className="card-item-title mb-2">
                      <label className="main-content-label tx-13 font-weight-bold mb-1">
                        {t("Today's Revenue")}
                      </label>
                    </div>
                    <div className="card-item-body">
                      <div className="card-item-stat">
                        <h4 className="font-weight-bold">
                          $
                          {showDashboardCount?.todayRevenue != null
                            ? Number(showDashboardCount.todayRevenue).toFixed(2)
                            : "0.00"}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
              <div className="card custom-card">
                <div className="card-body cp">
                  <div className="card-item">
                    <div className="card-item-icon card-icon cp bg-dark-transparent">
                      <i className="fa fa-history text-dark tx-22"></i>
                    </div>
                    <div className="card-item-title mb-2">
                      <label className="main-content-label tx-13 font-weight-bold mb-1">
                        {t("Total Games Played")}
                      </label>
                    </div>
                    <div className="card-item-body">
                      <div className="card-item-stat">
                        <h4 className="font-weight-bold">
                          {getStat(showDashboardCount?.totalGamesPlayed)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row row-sm animation_fade mg-t-20">
        <div className="col-sm-12 col-lg-7 col-xl-7">
          <div className="card custom-card h-100">
            <div className="card-header border-bottom-0 pb-0">
              <div className="d-flex justify-content-between">
                <label className="main-content-label tx-13 font-weight-bold mb-1">
                  {t("Recent Transactions")}
                </label>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table border-0 text-nowrap mb-0">
                  <thead>
                    <tr>
                      <th className="wd-20p">{t("Customer")}</th>
                      <th className="wd-20p">{t("Amount")}</th>
                      <th className="wd-20p">{t("Status")}</th>
                      <th className="wd-20p">{t("Date")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted">
                          {t("Loading...")}
                        </td>
                      </tr>
                    ) : (
                      showDashboardCount?.recentInvoices?.map(
                        (invoice, index) => (
                          <tr key={index} className="border-bottom">
                            <td>
                              <div className="d-flex border-0">
                                <span className="font-weight-normal my-auto">
                                  {invoice?.parent_id?.name ||
                                    invoice?.user_id?.name ||
                                    "N/A"}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="font-weight-bold">
                                $
                                {Number(
                                  invoice?.amount ?? invoice?.plan_price ?? 0,
                                ).toFixed(2)}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge badge-${getTransactionStatusClass(invoice?.status)}-light`}
                              >
                                {invoice?.status}
                              </span>
                            </td>
                            <td>
                              <span>
                                {invoice?.createdAt
                                  ? new Date(
                                    invoice.createdAt,
                                  ).toLocaleDateString()
                                  : "—"}
                              </span>
                            </td>
                          </tr>
                        ),
                      )
                    )}
                    {!loading &&
                      (!showDashboardCount?.recentInvoices ||
                        showDashboardCount.recentInvoices.length === 0) && (
                        <tr>
                          <td
                            colSpan="4"
                            className="text-center py-4 text-muted"
                          >
                            {t("No recent transactions found")}
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-lg-5 col-xl-5">
          <div className="card custom-card">
            <div className="card-body">
              <ReactApexChart
                options={revenueChartOptions}
                series={revenueChartSeries}
                type="area"
                height={300}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
